import React, {
  Outlet,
  useNavigate,
  useLocation,
  useSearchParams,
  Link,
} from "react-router-dom";
import styled from "@emotion/styled";
import {
  Layout as AntLayout,
  Menu,
  Select,
  Button,
  Drawer,
  message,
  Tour,
  Dropdown,
  Tooltip,
  Badge,
  Modal,
} from "antd";
import {
  UploadOutlined,
  UserOutlined,
  BookOutlined,
  FileTextOutlined,
  MenuUnfoldOutlined,
  MenuFoldOutlined,
  CustomerServiceOutlined,
  HomeOutlined,
  DashboardOutlined,
  PlusOutlined,
  CloseOutlined,
  TrophyOutlined,
  LogoutOutlined,
  ContainerOutlined,
  BankOutlined,
  CalculatorOutlined,
  ShopOutlined,
  SwapOutlined,
  CarryOutOutlined,
  GlobalOutlined,
  ShoppingOutlined,
  QuestionCircleOutlined,
  RocketOutlined,
  BellOutlined,
  CloudOutlined,
  CalendarOutlined,
  ToolOutlined,
} from "@ant-design/icons";
import { supabase } from "../lib/supabase";
import { useEffect, useState } from "react";
import { useCompanies } from "../hooks/useCompanies";
import { useCompany } from "../hooks/useCompany";
import { ChatbaseButton } from "./ChatbaseButton";
import { QRCodeButton } from "./QRCodeButton";
import { RestoreSession } from "./RestoreSession";
import { useUser } from "../hooks/useUser";
import { LanguageSwitcher } from "./LanguageSwitcher";
import { useTranslation } from "react-i18next";
import { UpdateNotification } from "./UpdateNotification";
import { usePostHogUser } from "../hooks/usePostHogUser";
import { PageViewTracker } from "./PageViewTracker";
import { FeatureFlagInitializer } from "./FeatureFlagInitializer";
import { AdminMenu } from "./AdminMenu";
import { AdminBanner } from "./AdminBanner";
import { AnnouncementBanners } from "./AnnouncementBanners";
import { ChannelDockRegisterSidebarNotice } from "./ChannelDockRegisterSidebarNotice";
import { CUSTOMS_PAGE_ENABLED } from "../constants/leancustoms";
import { useIsAdmin } from "../hooks/useIsAdmin";
import type { MenuProps, TourProps } from "antd";

/** ChannelDock public portal for all users (not tied to company `wms_provider`). */
const FULFILMENT_PORTAL_URL = "https://channeldock.com/portal/public/";

const { Sider, Content, Header } = AntLayout;

const StyledLayout = styled(AntLayout)`
  min-height: 100vh;
  height: 100%;
  overflow-x: hidden;
`;

const StyledContent = styled(Content)<{ "data-noauth"?: string }>`
  background: white;
  margin: 0;
  padding: ${(props) => (props["data-noauth"] ? "0" : "24px")};
  min-height: 280px;
  border-radius: 4px;
  width: 100%;
  overflow-x: hidden;
  overflow-y: auto;
  flex: 1;
`;

const StyledHeader = styled(Header)`
  display: flex;
  justify-content: space-between;
  align-items: center;
  background: white;
  padding: 0 24px;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.1);
  position: sticky;
  top: 0;
  z-index: 1000;
  width: 100%;
  margin: 0;
`;

const Logo = styled.div<{ $collapsed?: boolean }>`
  height: 52px;
  margin: 12px;
  padding-top: 8px;
  text-align: center;
  transition: all 0.3s ease;

  img {
    height: 100%;
    width: auto;
    max-width: ${(props) => (props.$collapsed ? "40px" : "100%")};
    object-fit: contain;
    transition: max-width 0.3s ease;
  }
`;

const CompanySelect = styled(Select)`
  margin: 16px;
  width: calc(100% - 32px);

  .ant-select-selector {
    background-color: rgba(255, 255, 255, 0.1) !important;
    color: white !important;
  }

  .ant-select-selection-placeholder,
  .ant-select-arrow {
    color: rgba(255, 255, 255, 0.65) !important;
  }
`;

const SingleCompanyDisplay = styled.div`
  margin: 16px;
  padding: 8px 12px;
  background-color: rgba(255, 255, 255, 0.1);
  border-radius: 4px;
  color: white;
  display: flex;
  align-items: center;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
  border: 1px solid rgba(255, 255, 255, 0.15);
  transition: all 0.3s ease;

  &:hover {
    background-color: rgba(255, 255, 255, 0.15);
    border-color: rgba(255, 255, 255, 0.25);
  }
`;

const CompanyIcon = styled(ToolOutlined)`
  margin-right: 8px;
  font-size: 16px;
`;

const CompanyName = styled.div`
  font-size: 14px;
  font-weight: 500;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

// Add a new styled component for the header actions
const HeaderActions = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
`;

const StyledSider = styled(Sider)`
  height: 100vh;
  position: fixed;
  left: 0;
  overflow: hidden;
  display: flex;
  flex-direction: column;

  @media (max-width: 1200px) {
    display: none;
  }
`;

const MenuContainer = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  overflow: auto;
  height: calc(100vh - 164px);
`;

const MainMenuWrapper = styled.div`
  flex: 1;
  overflow-y: auto;
  padding-bottom: 16px;
`;

const BottomMenuWrapper = styled.div`
  border-top: 1px solid rgba(255, 255, 255, 0.1);
  margin-top: auto;
  padding-bottom: 20px;
  position: relative;
  z-index: 1;
`;

const MobileLogo = styled.img`
  height: 32px;
  margin-left: 16px;
  display: none;
  @media (max-width: 1200px) {
    display: block;
    opacity: ${({ $isMenuOpen }: { $isMenuOpen: boolean }) =>
      $isMenuOpen ? "0" : "1"};
    transition: opacity 0.3s ease;
  }
`;

const MobileMenuButton = styled(Button)`
  display: none;
  @media (max-width: 1200px) {
    display: flex;
    align-items: center;
    color: rgba(0, 0, 0, 0.65);
    padding: 0;
    height: auto;
    &:hover,
    &:focus {
      color: rgba(0, 0, 0, 0.85);
    }
  }
`;

const MobileDrawer = styled(Drawer)`
  .ant-drawer-body {
    padding: 0;
    background: #001529;
    height: 100%;
    display: flex;
    flex-direction: column;
  }
`;

const MainContentWrapper = styled.div`
  margin-left: 200px;
  transition: margin-left 0.3s ease;
  width: calc(100% - 200px);
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  position: relative;

  &.collapsed {
    margin-left: 80px;
    width: calc(100% - 80px);
  }

  @media (max-width: 1200px) {
    margin-left: 0;
    width: 100%;
  }
`;

// Create styled footer component
const Footer = styled.div`
  text-align: center;
  padding: 12px;
  font-size: 12px;
  color: #888;
  margin-top: 20px;
  background: #f0f2f5;
  border-top: 1px solid #eee;
`;

export const Layout = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const { user, isImpersonating } = useUser();
  const { companies, loading: loadingCompanies } = useCompanies();
  const { selectedCompany, setSelectedCompany, companyDetails } = useCompany();
  const { t } = useTranslation();
  const [collapsed, setCollapsed] = useState(false);
  const [mobileMenuVisible, setMobileMenuVisible] = useState(false);
  const [showPasswordTour, setShowPasswordTour] = useState(false);
  const [tourOpen, setTourOpen] = useState(false);
  const [showUpdates, setShowUpdates] = useState(false);

  // Check if current user is admin (from database)
  const { isAdmin: userIsAdmin } = useIsAdmin();
  const defaultWmsName =
    companyDetails?.wms_provider === "channeldock" ? "ChannelDock" : "Picqer";
  const wmsOrdersLabel = t("common.wmsOrders", { provider: defaultWmsName });

  // Track user in PostHog with company information
  usePostHogUser(selectedCompany);

  useEffect(() => {
    if (companies.length > 0 && !selectedCompany) {
      setSelectedCompany(companies[0].id);
    }
  }, [companies, selectedCompany, setSelectedCompany]);

  useEffect(() => {
    // Check if user just logged in via magic link
    const justLoggedIn = location.pathname === "/auth/callback";
    if (justLoggedIn) {
      setShowPasswordTour(true);
    }
  }, [location]);

  useEffect(() => {
    const recoveryLogin = searchParams.get("recovery_login");
    if (recoveryLogin === "true") {
      setShowPasswordTour(true);
      // Remove the parameter from URL to prevent tour showing on refresh
      searchParams.delete("recovery_login");
      window.history.replaceState(
        {},
        "",
        `${location.pathname}${
          searchParams.toString() ? "?" + searchParams.toString() : ""
        }`
      );
    }
  }, [location, searchParams]);

  const handleSignOut = async () => {
    try {
      // Clear any impersonation data
      localStorage.removeItem("impersonatedUserId");
      localStorage.removeItem("originalUserId");

      // Track sign out event in PostHog
      const { trackAuthEvent, resetUser } = await import("../lib/posthog");
      trackAuthEvent("sign_out", {
        method: "manual",
        userId: user?.id,
      });

      const { error } = await supabase.auth.signOut();
      if (error) throw error;

      // Reset PostHog user after signout
      resetUser();

      message.success("Signed out successfully");
      navigate("/auth");
    } catch (error: unknown) {
      // Capture the error in PostHog
      const { captureError } = await import("../lib/posthog");
      captureError(
        error,
        {
          component: "Layout",
          action: "signOut",
          userId: user?.id,
        },
        "auth_signout"
      );

      message.error(
        error instanceof Error ? error.message : "An error occurred"
      );
    }
  };

  const tourSteps: TourProps["steps"] = [
    {
      title: t("tour.welcomeBack"),
      description: t("tour.updatePasswordPrompt"),
      target: () =>
        document.querySelector(".ant-menu-item-account") as HTMLElement,
      nextButtonProps: {
        children: t("tour.goToAccount"),
      },
      onNext: () => {
        navigate("/account");
        setShowPasswordTour(false);
      },
    },
  ];

  if (!user || location.pathname === "/auth") {
    return (
      <StyledLayout>
        <StyledContent data-noauth="true" style={{ width: "100%", margin: 0 }}>
          <Outlet />
        </StyledContent>

        {/* Privacy Policy Footer for non-auth pages */}
        <Footer style={{ position: "fixed", bottom: 0, width: "100%" }}>
          <Link
            to="/privacy-policy"
            style={{ color: "inherit" }}
            target="_blank"
            rel="noopener noreferrer"
          >
            Privacy Policy
          </Link>{" "}
          | e-freight forwarding® {new Date().getFullYear()}
        </Footer>
      </StyledLayout>
    );
  }

  return (
    <StyledLayout>
      <FeatureFlagInitializer />
      <PageViewTracker />

      {/* Admin Banner at the top of the page */}
      <AdminBanner />

      {/* Desktop Sider */}
      <StyledSider
        collapsible
        collapsed={collapsed}
        onCollapse={(value) => setCollapsed(value)}
        theme="dark"
      >
        <Logo $collapsed={collapsed}>
          <img src="/logo.png" alt="Logo" />
        </Logo>
        {companies.length > 1 ? (
          <CompanySelect
            loading={loadingCompanies}
            value={selectedCompany}
            onChange={(value) => {
              if (typeof value === "number") {
                setSelectedCompany(value);
                if (location.pathname !== "/dashboard") {
                  navigate("/dashboard");
                }
              }
            }}
            options={companies.map((company) => ({
              value: company.id,
              label: company.name,
            }))}
            placeholder={t("common.selectCompany")}
            showSearch
            filterOption={(input, option) =>
              (option?.label ?? "").toLowerCase().includes(input.toLowerCase())
            }
            optionFilterProp="label"
            notFoundContent={t("common.noCompaniesFound")}
          />
        ) : companies.length === 1 ? (
          <SingleCompanyDisplay>
            <CompanyIcon />
            <CompanyName>{companies[0].name}</CompanyName>
          </SingleCompanyDisplay>
        ) : null}
        <MenuContainer>
          <MainMenuWrapper>
            <Menu
              theme="dark"
              mode="inline"
              selectedKeys={[location.pathname.split("/")[1] || "dashboard"]}
              items={[
                {
                  key: "dashboard",
                  icon: <DashboardOutlined />,
                  label: t("common.dashboard"),
                  onClick: () => navigate("/dashboard"),
                },
                {
                  key: "bookings",
                  icon: <CalendarOutlined />,
                  label: t("common.bookings"),
                  onClick: () => navigate("/bookings"),
                },
                {
                  key: "my-products",
                  icon: <ShoppingOutlined />,
                  label: t("common.myProducts"),
                  onClick: () => navigate("/my-products"),
                },
                CUSTOMS_PAGE_ENABLED &&
                companyDetails?.leancustoms_organization_id &&
                companyDetails?.leancustoms_contract_id
                  ? {
                      key: "customs",
                      icon: <GlobalOutlined />,
                      label: t("common.customs"),
                      onClick: () => navigate("/customs"),
                    }
                  : null,
                companyDetails?.moneybird_contact_id
                  ? {
                      key: "invoices",
                      icon: <FileTextOutlined />,
                      label: t("common.invoices"),
                      onClick: () => navigate("/invoices"),
                    }
                  : null,
                (companyDetails?.wms_provider === "picqer" && companyDetails?.picqer_enabled) ||
                (companyDetails?.wms_provider === "channeldock" && companyDetails?.channeldock_enabled)
                  ? {
                      key: "wms-orders",
                      icon: <ContainerOutlined />,
                      label: wmsOrdersLabel,
                      onClick: () => navigate("/wms-orders"),
                    }
                  : null,
                {
                  key: "tools",
                  icon: <ToolOutlined />,
                  label: t("common.tools"),
                  onClick: () => navigate("/tools"),
                },
                {
                  key: "company-info",
                  icon: <BankOutlined />,
                  label: t("common.companyInfo"),
                  onClick: () => navigate("/company-info"),
                },
                {
                  key: "fulfilment-portal",
                  icon: <CloudOutlined />,
                  label: t("common.fulfilmentPortal"),
                  onClick: () => {
                    window.open(FULFILMENT_PORTAL_URL, "_blank");
                  },
                },
              ].filter(Boolean)}
            />
          </MainMenuWrapper>
          <ChannelDockRegisterSidebarNotice collapsed={collapsed} />
          <BottomMenuWrapper>
            <Menu
              theme="dark"
              mode="inline"
              items={[
                {
                  key: "account",
                  icon: <UserOutlined />,
                  label: t("common.account"),
                  onClick: () => navigate("/account"),
                },
                isImpersonating
                  ? {
                      key: "restore",
                      label: <RestoreSession />,
                    }
                  : null,
                {
                  key: "signout",
                  icon: <LogoutOutlined />,
                  label: t("common.signOut"),
                  onClick: handleSignOut,
                  danger: true,
                },
              ].filter(Boolean)}
            />
          </BottomMenuWrapper>
        </MenuContainer>
      </StyledSider>

      <MainContentWrapper className={collapsed ? "collapsed" : ""}>
        <StyledHeader>
          <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
            <MobileMenuButton
              type="text"
              icon={<MenuUnfoldOutlined />}
              onClick={() => setMobileMenuVisible(true)}
            />
            <MobileLogo
              src="/logo_black.png"
              alt="Logo"
              $isMenuOpen={mobileMenuVisible}
            />
          </div>
          <HeaderActions>
            {userIsAdmin && <AdminMenu />}
            <UpdateNotification />
            <LanguageSwitcher />
          </HeaderActions>
        </StyledHeader>

        {/* WWV promo banner – full width, above announcements */}
        {/* Admin-configured announcement banners (multiple can be live) */}
        <AnnouncementBanners />

        <StyledContent>
          <Outlet />
        </StyledContent>

        {/* Privacy Policy Footer */}
        <Footer>
          <Link
            to="/privacy-policy"
            style={{ color: "inherit" }}
            target="_blank"
            rel="noopener noreferrer"
          >
            Privacy Policy
          </Link>{" "}
          | ® E-Freight Forwarding {new Date().getFullYear()}
        </Footer>
      </MainContentWrapper>

      {/* Mobile Menu Drawer */}
      <MobileDrawer
        placement="left"
        open={mobileMenuVisible}
        onClose={() => setMobileMenuVisible(false)}
        closable={false}
      >
        <Logo $collapsed={false}>
          <img src="/logo.png" alt="Logo" />
        </Logo>
        {companies.length > 1 ? (
          <CompanySelect
            loading={loadingCompanies}
            value={selectedCompany}
            onChange={(value) => {
              if (typeof value === "number") {
                setSelectedCompany(value);
                if (location.pathname !== "/dashboard") {
                  navigate("/dashboard");
                }
              }
              setMobileMenuVisible(false);
            }}
            options={companies.map((company) => ({
              value: company.id,
              label: company.name,
            }))}
            placeholder={t("common.selectCompany")}
            showSearch
            filterOption={(input, option) =>
              (option?.label ?? "").toLowerCase().includes(input.toLowerCase())
            }
            optionFilterProp="label"
            notFoundContent={t("common.noCompaniesFound")}
          />
        ) : companies.length === 1 ? (
          <SingleCompanyDisplay>
            <CompanyIcon />
            <CompanyName>{companies[0].name}</CompanyName>
          </SingleCompanyDisplay>
        ) : null}
        <MenuContainer>
          <MainMenuWrapper>
            <Menu
              theme="dark"
              mode="inline"
              selectedKeys={[location.pathname.split("/")[1] || "dashboard"]}
              items={[
                {
                  key: "dashboard",
                  icon: <DashboardOutlined />,
                  label: t("common.dashboard"),
                  onClick: () => {
                    navigate("/dashboard");
                    setMobileMenuVisible(false);
                  },
                },
                {
                  key: "bookings",
                  icon: <CalendarOutlined />,
                  label: t("common.bookings"),
                  onClick: () => {
                    navigate("/bookings");
                    setMobileMenuVisible(false);
                  },
                },
                {
                  key: "my-products",
                  icon: <ShoppingOutlined />,
                  label: t("common.myProducts"),
                  onClick: () => {
                    navigate("/my-products");
                    setMobileMenuVisible(false);
                  },
                },
                CUSTOMS_PAGE_ENABLED &&
                companyDetails?.leancustoms_organization_id &&
                companyDetails?.leancustoms_contract_id
                  ? {
                      key: "customs",
                      icon: <GlobalOutlined />,
                      label: t("common.customs"),
                      onClick: () => {
                        navigate("/customs");
                        setMobileMenuVisible(false);
                      },
                    }
                  : null,
                companyDetails?.moneybird_contact_id
                  ? {
                      key: "invoices",
                      icon: <FileTextOutlined />,
                      label: t("common.invoices"),
                      onClick: () => {
                        navigate("/invoices");
                        setMobileMenuVisible(false);
                      },
                    }
                  : null,
                (companyDetails?.wms_provider === "picqer" && companyDetails?.picqer_enabled) ||
                (companyDetails?.wms_provider === "channeldock" && companyDetails?.channeldock_enabled)
                  ? {
                      key: "wms-orders",
                      icon: <ContainerOutlined />,
                      label: wmsOrdersLabel,
                      onClick: () => {
                        navigate("/wms-orders");
                        setMobileMenuVisible(false);
                      },
                    }
                  : null,
                {
                  key: "tools",
                  icon: <ToolOutlined />,
                  label: t("common.tools"),
                  onClick: () => {
                    navigate("/tools");
                    setMobileMenuVisible(false);
                  },
                },
                {
                  key: "company-info",
                  icon: <BankOutlined />,
                  label: t("common.companyInfo"),
                  onClick: () => {
                    navigate("/company-info");
                    setMobileMenuVisible(false);
                  },
                },
                {
                  key: "fulfilment-portal",
                  icon: <CloudOutlined />,
                  label: t("common.fulfilmentPortal"),
                  onClick: () => {
                    window.open(FULFILMENT_PORTAL_URL, "_blank");
                    setMobileMenuVisible(false);
                  },
                },
              ].filter(Boolean)}
            />
          </MainMenuWrapper>
          <ChannelDockRegisterSidebarNotice />
          <BottomMenuWrapper>
            <Menu
              theme="dark"
              mode="inline"
              items={[
                {
                  key: "account",
                  icon: <UserOutlined />,
                  label: t("common.account"),
                  onClick: () => {
                    navigate("/account");
                    setMobileMenuVisible(false);
                  },
                },
                isImpersonating
                  ? {
                      key: "restore",
                      label: <RestoreSession />,
                      onClick: () => setMobileMenuVisible(false),
                    }
                  : null,
                {
                  key: "signout",
                  icon: <LogoutOutlined />,
                  label: t("common.signOut"),
                  onClick: () => {
                    handleSignOut();
                    setMobileMenuVisible(false);
                  },
                  danger: true,
                },
              ].filter(Boolean)}
            />
          </BottomMenuWrapper>
        </MenuContainer>
      </MobileDrawer>

      {/* Conditionally render QRCodeButton or ChatbaseButton */}
      {user?.email === "demo@efreightforwarding.com" ? (
        <QRCodeButton />
      ) : (
        <ChatbaseButton />
      )}

      <Tour
        open={showPasswordTour}
        onClose={() => setShowPasswordTour(false)}
        steps={tourSteps}
      />
    </StyledLayout>
  );
};
